/// <reference types="@types/react-router-dom" />

import * as React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import IconButton from '@mui/material/IconButton';
import { useAuth0, Auth0Provider } from '@auth0/auth0-react';
import { Auth0ContextInterface } from '@auth0/auth0-react/src/auth0-context';
import ROLE from './roles';
import UserLinkProfile from '../../components/Header/UserLinkProfile';

const StyledLogContainer = styled.div`
  display: flex;
  flex-direction: row;
  color: #ffffff;
`;

interface AuthContextType extends Auth0ContextInterface {
  logoutWithRedirect(): void;
}

export const useProvideAuth = () => {
  const { user, isAuthenticated, loginWithRedirect, logout, getAccessTokenSilently } = useAuth0();

  const logoutWithRedirect = () =>
    logout({
      returnTo: `${window.location.origin}`,
    });

  return {
    user,
    logoutWithRedirect,
    loginWithRedirect,
    isAuthenticated,
    getAccessTokenSilently,
  } as AuthContextType;
};

export const RequireAuth = ({ children, roles }: { children: JSX.Element; roles: Array<ROLE> }) => {
  const { isAuthenticated, user } = useProvideAuth();
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} />;
  }
  // console.log('user', user); // eslint-disable-line no-console
  const userHasRequiredRole = !!(user && roles.includes(user?.[`${process.env.REACT_APP_METADATA_FOR_ROLES}/roles`]?.[0]));
  // console.log('roles', roles); // eslint-disable-line no-console

  if (isAuthenticated && !userHasRequiredRole) {
    return <h1>Access Denied!</h1>;
  }
  return children;
};

export const AuthStatus = ({ children }: { children: React.ReactNode }) => {
  // const [userMetadata, setUserMetadata] = React.useState<string | null>(null);
  const { logoutWithRedirect, loginWithRedirect, isAuthenticated, user } = useProvideAuth();
  const onLogoutHandler = React.useCallback(() => {
    logoutWithRedirect();
    sessionStorage.removeItem('currentPosition');
  }, [logoutWithRedirect]);
  const onLoginHandler = React.useCallback(() => loginWithRedirect(), [loginWithRedirect]);

  // useEffect(() => {
  //   const getUserDate = async (): Promise<void> => {
  //     // const domain = 'localhost:4000';
  //     try {
  //       const accessToken = await getAccessTokenSilently();
  //       // const userDetailsByIdUrl = `https://${domain}/api/v2/oauth/token`
  //       // const metadataResponse = await fetch(userDetailsByIdUrl, {
  //       //   headers: {
  //       //     Authorization: `Bearer ${accessToken}`,
  //       //   },
  //       // });
  //       // const obj = await metadataResponse.json();
  //       // setUserMetadata(accessToken);
  //     } catch (e: any) {
  //       console.log(e.message); // eslint-disable-line no-console
  //     }
  //   };
  //   getUserDate();
  // }, [getAccessTokenSilently, user?.sub]);

  if (!isAuthenticated) {
    return <IconButton onClick={onLoginHandler}>{children}</IconButton>;
  }

  return (
    <StyledLogContainer>
      {isAuthenticated && <UserLinkProfile user={user} />}
      <IconButton onClick={onLogoutHandler}>{children}</IconButton>
    </StyledLogContainer>
  );
};

export const Auth0ProviderWithNavigate = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();

  const onRedirectCallback = () => {
    navigate('dashboard' || window.location.pathname);
  };

  return (
    <Auth0Provider
      domain={process.env.REACT_APP_DOMAIN ?? ''}
      clientId={process.env.REACT_APP_CLIENT_ID ?? ''}
      redirectUri={window.location.origin}
      audience={`https://${process.env.REACT_APP_DOMAIN}/api/v2/`}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
};
